import { FC } from 'react';
import Span from './components/Span';
import styled from 'styled-components';
import { fabric } from 'fabric';
import { useFabricEditor } from '../../lib/FabricJs';
import { placeNearSlideCenter } from '../../utils/placeNearSlideCenter';
import {
  shapeDefinitions,
  ShapeDefinition,
} from '../../utils/shapeDefinitions';

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  height: 64px;
  width: 64px;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  &:hover {
    background-color: #dcdcdc;
  }
`;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  align-content: flex-start;
  gap: 4px;
  background-color: white;
  border-radius: 4px;
  padding: 8px;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
`;

const ShapePreview = ({ shape }: { shape: ShapeDefinition }) => (
  <svg viewBox="0 0 100 100" width="40" height="40">
    {shape.kind === 'path' && (
      <path d={shape.d} fill="black" fillRule={shape.fillRule || 'nonzero'} />
    )}
    {shape.kind === 'rect' && (
      <rect
        x={(100 - shape.width) / 2}
        y={(100 - shape.height) / 2}
        width={shape.width}
        height={shape.height}
        rx={shape.rx}
        ry={shape.ry}
        fill="black"
      />
    )}
    {shape.kind === 'circle' && (
      <circle cx={50} cy={50} r={shape.radius} fill="black" />
    )}
    {shape.kind === 'ellipse' && (
      <ellipse cx={50} cy={50} rx={shape.rx} ry={shape.ry} fill="black" />
    )}
  </svg>
);

const ShapeWidget: FC = () => {
  const { canvas } = useFabricEditor();

  const onAddShape = (shape: ShapeDefinition) => {
    if (!canvas) return;
    let object: fabric.Object;

    switch (shape.kind) {
      case 'rect':
        object = new fabric.Rect({
          width: shape.width,
          height: shape.height,
          rx: shape.rx,
          ry: shape.ry,
          angle: 0,
          fill: 'black',
        });
        break;
      case 'circle':
        object = new fabric.Circle({
          radius: shape.radius,
          fill: 'black',
        });
        break;
      case 'ellipse':
        object = new fabric.Ellipse({
          rx: shape.rx,
          ry: shape.ry,
          fill: 'black',
        });
        break;
      case 'path':
      default:
        object = new fabric.Path(shape.d, {
          fill: 'black',
          fillRule: shape.fillRule || 'nonzero',
          // The player (preview/utils.ts) renders shapes as an <img> from
          // an inline SVG data URI, since fabric's serialized JSON stores
          // `path` as a parsed command array, not the original "d" string -
          // keep the original string + fill rule around on `data` (which is
          // preserved through slide save/load) so playback can rebuild it.
          data: {
            svgPath: shape.d,
            fillRule: shape.fillRule || 'nonzero',
          },
        });
        break;
    }

    placeNearSlideCenter(canvas, object);
    canvas.add(object);
    canvas.setActiveObject(object);
    canvas.requestRenderAll();
  };

  return (
    <>
      <Span>Lines & Shapes</Span>
      <Container>
        {shapeDefinitions.map((shape, index) => (
          <ImageContainer
            key={index}
            role="banner"
            aria-label={shape.title}
            title={shape.title}
            onClick={() => onAddShape(shape)}
          >
            <ShapePreview shape={shape} />
          </ImageContainer>
        ))}
      </Container>
    </>
  );
};

export default ShapeWidget;
